<template>
    <div class="min-h-screen bg-[#071226] relative overflow-hidden">
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
            <div class="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.18),transparent_30%)]"></div>
            <div class="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(249,115,22,0.12),transparent_30%)]"></div>

            <div
                v-for="particle in particles"
                :key="particle.id"
                class="absolute rounded-full bg-white/5 animate-float-particle"
                :style="particle.style"
            ></div>

            <div class="absolute top-20 left-[10%] w-64 h-64 bg-orange-500/20 rounded-full filter blur-3xl animate-pulse-slow"></div>
            <div class="absolute bottom-20 right-[5%] w-96 h-96 bg-blue-600/20 rounded-full filter blur-3xl animate-pulse-slow animation-delay-2000"></div>
            <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-500/10 rounded-full filter blur-3xl animate-pulse-slow animation-delay-4000"></div>

            <svg class="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <pattern id="ts-auth-grid" width="60" height="60" patternUnits="userSpaceOnUse">
                        <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" stroke-width="0.5"/>
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#ts-auth-grid)" />
            </svg>
        </div>

        <div class="relative z-10 min-h-screen flex items-center">
            <div class="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
                <div class="grid lg:grid-cols-2 gap-10 items-center">
                    <div class="hidden lg:block animate-fade-in-up">
                        <div class="mb-8 flex justify-center animate-float">
                            <a href="/" class="inline-block">
                                <img
                                    src="/imgs/tecno/logo_border_blanco.png"
                                    alt="Tecnosinergia"
                                    class="h-16 w-auto transition-transform duration-300 hover:scale-105"
                                />
                            </a>
                        </div>

                        <div class="max-w-xl">
                            <div class="mb-6 inline-flex items-center gap-2 rounded-full border border-orange-500/20 bg-orange-500/10 px-4 py-2 text-sm font-medium text-orange-400">
                                <span class="relative flex h-2 w-2">
                                    <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-75"></span>
                                    <span class="relative inline-flex h-2 w-2 rounded-full bg-orange-500"></span>
                                </span>
                                Acceso interno Tecnosinergia
                            </div>

                            <h1 class="mb-5 text-5xl font-black leading-tight text-white">
                                Centro de
                                <span class="bg-gradient-to-r from-orange-500 to-orange-400 bg-clip-text text-transparent">
                                    {{ brandTitle }}

                                </span>
                            </h1>
                            <p class="mb-8 text-lg leading-relaxed text-slate-300">
                                {{ brandSubtitle }}
                            </p>

                            <div class="mb-8 grid grid-cols-3 gap-4">
                                <div
                                    v-for="(stat, idx) in stats"
                                    :key="idx"
                                    class="rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:-translate-y-1 hover:bg-white/10"
                                >
                                    <div class="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500/15 text-orange-400">
                                        <component :is="stat.icon" class="h-4 w-4" />
                                    </div>
                                    <p class="text-2xl font-black text-white">{{ stat.value }}</p>
                                    <p class="mt-1 text-xs text-slate-400">{{ stat.label }}</p>
                                </div>
                            </div>

                            <div class="space-y-4">
                                <div
                                    v-for="(item, index) in benefits"
                                    :key="index"
                                    class="group flex items-start gap-4"
                                >
                                    <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-orange-500/20 bg-orange-500/10 text-orange-400 transition group-hover:scale-110 group-hover:bg-orange-500/20">
                                        <component :is="item.icon" class="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h3 class="font-semibold text-white transition group-hover:text-orange-400">
                                            {{ item.title }}
                                        </h3>
                                        <p class="mt-1 text-sm text-slate-400">
                                            {{ item.text }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="w-full max-w-md mx-auto animate-fade-in-right">
                        <div class="lg:hidden text-center mb-8">
                            <a href="/" class="inline-block animate-float">
                                <img
                                    src="/imgs/tecno/logo_border_blanco.png"
                                    alt="Tecnosinergia"
                                    class="h-14 w-auto mx-auto transition-transform duration-300 hover:scale-105"
                                />
                            </a>
                            <p class="mt-4 text-sm text-slate-400">
                                Plataforma de descargas Tecnosinergia
                            </p>
                        </div>

                        <div class="bg-[#0f172a]/95 backdrop-blur-sm border border-white/10 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
                            <div class="p-8 border-b border-white/5">
                                <div class="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-6 shadow-lg shadow-orange-500/20">
                                    <slot name="icon" />
                                </div>
                                <h2 class="text-3xl font-black text-white mb-2">
                                    {{ title }}
                                </h2>
                                <p class="text-slate-400 leading-relaxed">
                                    {{ subtitle }}
                                </p>
                            </div>
                            <div class="p-8">
                                <slot />
                                <div class="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
                                    <ShieldCheckIcon class="h-4 w-4 text-emerald-500" />
                                    <span>Conexión segura · Acceso corporativo</span>
                                </div>
                            </div>
                        </div>

                        <div class="mt-6 grid grid-cols-3 gap-2 lg:hidden">
                            <div
                                v-for="(item, index) in mobileHighlights"
                                :key="index"
                                class="rounded-2xl border border-white/10 bg-white/5 px-2 py-3 text-center"
                            >
                                <component :is="item.icon" class="mx-auto h-5 w-5 text-orange-400" />
                                <p class="mt-2 text-[11px] font-semibold text-slate-300">{{ item.label }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    CloudDownloadIcon,
    ShieldCheckIcon,
    CollectionIcon,
    LightningBoltIcon,
    ClockIcon,
    LockClosedIcon,
} from '@heroicons/vue/outline';

export default {
    components: {
        CloudDownloadIcon,
        ShieldCheckIcon,
        CollectionIcon,
        LightningBoltIcon,
        ClockIcon,
        LockClosedIcon,
    },
    props: {
        title: { type: String, required: true },
        subtitle: { type: String, default: '' },
        brandTitle: { type: String, default: 'Descargas' },
        brandSubtitle: {
            type: String,
            default: 'Administra archivos, firmwares y recursos de marca desde un solo acceso interno.',
        },
    },
    data() {
        const particles = Array.from({ length: 30 }, (_, id) => {
            const size = Math.random() * 4 + 2;
            return {
                id,
                style: {
                    width: `${size}px`,
                    height: `${size}px`,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 10}s`,
                    animationDuration: `${Math.random() * 10 + 5}s`,
                },
            };
        });

        return {
            particles,
            stats: [
                { value: '1', label: 'Acceso', icon: 'LockClosedIcon' },
                { value: 'FTP', label: 'Almacenamiento', icon: 'CloudDownloadIcon' },
                { value: '24/7', label: 'Disponible', icon: 'ClockIcon' },
            ],
            benefits: [
                {
                    icon: 'CloudDownloadIcon',
                    title: 'Gestión de archivos',
                    text: 'Crea, edita y publica descargas para el portal de distribuidores.',
                },
                {
                    icon: 'CollectionIcon',
                    title: 'Marcas y recursos',
                    text: 'Organiza firmwares y materiales por marca de forma centralizada.',
                },
                {
                    icon: 'ShieldCheckIcon',
                    title: 'Acceso controlado',
                    text: 'Inicio de sesión corporativo con permisos por nivel de usuario.',
                },
                {
                    icon: 'LightningBoltIcon',
                    title: 'Operación ágil',
                    text: 'Exporta listados y actualiza archivos sin fricción.',
                },
            ],
            mobileHighlights: [
                { icon: 'CloudDownloadIcon', label: 'Archivos' },
                { icon: 'CollectionIcon', label: 'Marcas' },
                { icon: 'ShieldCheckIcon', label: 'Seguro' },
            ],
        };
    },
};
</script>

<style scoped>
@keyframes float {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    50% { transform: translateY(-10px) translateX(5px); }
}

@keyframes float-particle {
    0% { transform: translateY(0px) translateX(0px); opacity: 0; }
    50% { opacity: 0.5; }
    100% { transform: translateY(-100px) translateX(20px); opacity: 0; }
}

@keyframes pulse-slow {
    0%, 100% { opacity: 0.1; transform: scale(1); }
    50% { opacity: 0.2; transform: scale(1.1); }
}

.animate-float { animation: float 6s ease-in-out infinite; }
.animate-float-particle { animation: float-particle linear infinite; }
.animate-pulse-slow { animation: pulse-slow 8s ease-in-out infinite; }
.animation-delay-2000 { animation-delay: 2s; }
.animation-delay-4000 { animation-delay: 4s; }
</style>
